import { Component, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { clone } from 'lodash-es';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/core/services/auth.service';
import { HeaderService } from 'src/app/core/services/header.service';
import { MemberInformationService } from 'src/app/core/services/member-information.service';
import { PatientService } from 'src/app/core/services/patient.service';
import { SideNavService } from 'src/app/core/services/side-nav.service';
import { UserService } from 'src/app/core/services/user.service';
import { ExternalNavItem, SecondaryNavItem } from 'src/app/core/utils/routing-util';
import { isString } from 'src/app/core/utils/type-util';

@Component({
  selector: 'th-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss']
})
export class SideNavComponent implements OnInit, OnDestroy {
  isHoveringPrimaryNav = false;
  externalNavItems: ExternalNavItem[];
  secondaryNav: SecondaryNavItem[];
  externalNavItemsSubscription: Subscription;
  secondaryNavSubscription: Subscription;
  memberIdSubscription: Subscription;
  asmStatusUpdatedSubscription: Subscription;
  refreshHeaderTextSubscription: Subscription;
  memberId: string;
  loadingPatient = false;

  linkTitles = {
    myDashboard: 'My Dashboard',
    myWorklists: 'My Worklists',
    associatedPatients: 'Associated Patients',
    dmDashboard: 'Programs Dashboard',
    searchMembers: 'Search Members',
    bulkAssignMembers: 'Bulk Assign Members',
    manualPatientEntry: 'Manual Patient Entry',
    users: 'Users',
    reports: 'Reports',
    resources: 'Resources',
    manageResources: 'Manage Resources',
    maintainAssessments: 'Maintain Assessments',
    configureMessages: 'Configure Messages',
    configureAppNotifications: 'Configure Application Notifications',
    maintainWorklists: 'Maintain Worklists',
    messenger: 'Messenger',
    support: 'Support',
    requestNewMHNConnectUser: 'Request New MHNConnect User',
    updateProfile: 'Update Profile',
    userFeedback: 'Feedback',
    duplicateMembers: 'Duplicate  Members',
    systemTest: 'System Test',
    reportFiles: 'Sftp Report Files',
    logout: 'Logout',
    notifications: 'Notifications'
  };

  get showSecondaryNav(): boolean {
    return this.secondaryNav && this.secondaryNav.length && !this.isHoveringPrimaryNav;
  }

  constructor(
    public authService: AuthService,
    public sideNavService: SideNavService,
    public router: Router,
    private headerService: HeaderService,
    private memberInformationService: MemberInformationService,
    private patientService: PatientService,
    private userService: UserService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    this.externalNavItemsSubscription = this.sideNavService.externalNavItems.subscribe((externalNavItems) => {
      this.externalNavItems = externalNavItems;
    });

    this.secondaryNavSubscription = this.sideNavService.secondaryNav.subscribe((secondaryNav) => {
      this.secondaryNav = secondaryNav;

      this.memberIdSubscription?.unsubscribe();
      this.memberIdSubscription = this.memberInformationService.memberId.subscribe((memberId) => {
        this.memberId = memberId;

        this.updateHeaderText();
      });

      this.refreshHeaderTextSubscription?.unsubscribe();
      this.refreshHeaderTextSubscription = this.headerService.refreshCenterContentText.subscribe(() => {
        this.updateHeaderText();
      });
    });

    this.asmStatusUpdatedSubscription?.unsubscribe();

    this.asmStatusUpdatedSubscription = this.userService.userASMStatusUpdate.subscribe(() => {
      this.userService.getUser(this.authService.user.userId, false).subscribe((data) => {
        this.authService.user.asmStatus = clone(data).asmStatus;
      });
    });
  }

  ngOnDestroy() {
    this.externalNavItemsSubscription?.unsubscribe();
    this.secondaryNavSubscription?.unsubscribe();
    this.memberIdSubscription?.unsubscribe();
    this.asmStatusUpdatedSubscription?.unsubscribe();
  }

  onPrimaryNavMouseEnter() {
    this.isHoveringPrimaryNav = true;
  }

  onPrimaryNavMouseLeave() {
    this.isHoveringPrimaryNav = false;
  }

  isEntitled(navItem: SecondaryNavItem) {
    if (isString(navItem.entitlementUrl)) {
      return this.authService.isEntitled(navItem.entitlementUrl);
    } else if (navItem.entitlementUrl != null) {
      return navItem.entitlementUrl(this.authService);
    }

    return true;
  }

  updateHeaderText(secondaryNavItem: SecondaryNavItem = null) {
    const currentSecondaryNavItem = this.secondaryNav.find((navItem) => {
      if (secondaryNavItem) {
        return secondaryNavItem.routerLink === navItem.routerLink;
      }

      return navItem.routerLink === this.router.url;
    });

    if (this.memberId) {
      this.patientService.getPatientName(this.memberId, true).subscribe((patientName) => {
        if (currentSecondaryNavItem == null || currentSecondaryNavItem.displayText === 'Member Home') {
          this.headerService.setCenterContentText(patientName);

          this.patientService.getPatientPronoun(this.memberId).subscribe((preferredPronoun) => {
            this.headerService.setPronounText(preferredPronoun);
          });
        } else {
          this.headerService.setCenterContentText(`${patientName} / ${currentSecondaryNavItem.displayText}`);

          this.patientService.getPatientPronoun(this.memberId).subscribe((preferredPronoun) => {
            this.headerService.setPronounText(preferredPronoun);
          });
        }
      });
    } else {
      this.headerService.setCenterContentText('');
      this.headerService.setPronounText('');
    }
  }

  openModal(templateRef: TemplateRef<unknown>) {
    this.modalService.open(templateRef, { size: 'lg' });
  }
}
